import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import AIChat from "@/components/AIChat";
import SEO from "@/components/SEO";
import { ArrowLeft, Calendar, Users, Plane } from "lucide-react";

const DESTINATIONS = {
  goa: {
    name: "Goa",
    country: "India",
    tagline: "Sun, sand & susegad",
    image: "https://images.pexels.com/photos/16146954/pexels-photo-16146954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920",
    about: "From North Goa’s bohemian cafés to South Goa’s whispering beaches — a state with two personalities and room for both. Our curators know the shacks without queues and the sunrise points without crowds.",
    highlights: [
      "Private cruise on the Mandovi",
      "Dinner at a 300-year-old Portuguese home",
      "Morning yoga on a hidden southern beach",
      "Spice plantation lunch in the hinterland",
    ],
    best_time: "Oct – Feb",
    ideal_days: "4 – 6 days",
    ideal_for: "Couples, first-timers, long weekends",
  },
  kashmir: {
    name: "Kashmir",
    country: "India",
    tagline: "The valley of light",
    image: "https://images.unsplash.com/photo-1764362866160-457224e551f7?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85",
    about: "Houseboats on Dal Lake, meadows of Gulmarg, and saffron fields in Pampore — Kashmir in every season is a different country. We plan it with trusted local shikara-walas and homestays off the main circuit.",
    highlights: [
      "Two-night stay on a heritage houseboat",
      "Sunrise shikara through floating gardens",
      "Day trip to Tulip Gardens (Mar-Apr only)",
      "Skiing or gondola ride in Gulmarg",
    ],
    best_time: "Mar – Oct · Dec-Jan for snow",
    ideal_days: "6 – 8 days",
    ideal_for: "Honeymooners, families, photographers",
  },
  bali: {
    name: "Bali",
    country: "Indonesia",
    tagline: "Temples, terraces & tide pools",
    image: "https://images.unsplash.com/photo-1770185998570-db739db7af47?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85",
    about: "Our most-requested outbound destination. We pair Ubud’s temples and rice terraces with Seminyak’s beach clubs and Nusa Penida’s cliffs — crafted so you experience three Bali's, not one tourist trail.",
    highlights: [
      "Private driver across all zones",
      "Sunrise trek up Mount Batur",
      "Flower-petal floating breakfast",
      "Day cruise to Nusa Lembongan",
    ],
    best_time: "Apr – Oct",
    ideal_days: "7 – 10 days",
    ideal_for: "Honeymoons, yoga, families",
  },
  maldives: {
    name: "Maldives",
    country: "",
    tagline: "One island, one resort",
    image: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920",
    about: "We work directly with 40+ resorts across North Malé, South Atolls, and Baa Biosphere Reserve — so we can match your budget to the right water villa, the right house reef, and the right dinner-on-the-sandbar.",
    highlights: [
      "Transfer by seaplane (if atoll is remote)",
      "Overwater villa with private pool",
      "Guided snorkel with manta rays",
      "Private sandbar lunch",
    ],
    best_time: "Nov – Apr",
    ideal_days: "5 – 7 days",
    ideal_for: "Honeymoons, anniversaries",
  },
};

export default function Destination() {
  const { slug } = useParams();
  const dest = DESTINATIONS[slug?.toLowerCase()];
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    const openChat = () => setChatOpen(true);
    window.addEventListener("open-ai-chat", openChat);
    return () => window.removeEventListener("open-ai-chat", openChat);
  }, []);

  if (!dest) {
    return (
      <div className="min-h-screen bg-[var(--vp-bg)]">
        <Navbar variant="solid" />
        <div className="max-w-3xl mx-auto text-center py-32 px-6">
          <h1 className="font-serif-vp text-4xl text-[var(--vp-primary)] mb-4">Destination not found</h1>
          <Link to="/" className="vp-btn-outline inline-block">← Back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--vp-bg)]" data-testid="destination-page">
      <SEO
        title={`${dest.name} Holiday Packages 2026 from Ludhiana | Vacations Pro`}
        description={`Plan a customised ${dest.name} trip with Vacations Pro — Ludhiana's best travel agency since 2004. ${dest.tagline}. Best time: ${dest.best_time}. Ideal duration: ${dest.ideal_days}. Direct flights, offline-negotiated rates, 4.8★ on Google.`}
        path={`/destinations/${slug}`}
        image={dest.image}
        schema={{
          "@context": "https://schema.org",
          "@type": "TouristTrip",
          "name": `${dest.name} Holiday Package — Vacations Pro`,
          "description": dest.about,
          "image": dest.image,
          "touristType": dest.ideal_for,
          "itinerary": (dest.highlights || []).map((h) => ({
            "@type": "TouristAttraction",
            "name": h,
          })),
          "provider": {
            "@type": "TravelAgency",
            "name": "Vacations Pro International Pvt. Ltd.",
            "telephone": "+91-9501030117",
            "email": "alerts@vacationspro.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "K-10 Tower, Feroze Gandhi Market",
              "addressLocality": "Ludhiana",
              "addressRegion": "Punjab",
              "postalCode": "141001",
              "addressCountry": "IN",
            },
            "url": "https://vacationspro.com",
          },
          "departureLocation": {
            "@type": "City",
            "name": "Ludhiana",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Ludhiana",
              "addressRegion": "Punjab",
              "addressCountry": "IN",
            },
          },
          "arrivalLocation": {
            "@type": "City",
            "name": dest.name,
            "address": {
              "@type": "PostalAddress",
              "addressCountry": dest.country || "IN",
            },
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "INR",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "priceCurrency": "INR",
              "minPrice": "30000",
              "description": `Custom ${dest.name} package — exact pricing on enquiry`,
            },
            "availability": "https://schema.org/InStock",
            "validFrom": new Date().toISOString().split("T")[0],
            "url": `https://vacationspro.com/destinations/${slug}`,
          },
        }}
      />
      <Navbar variant="hero" />
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] w-full">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${dest.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/35" />
        <div className="relative z-10 h-full flex items-end px-6 md:px-12 pb-16 text-white">
          <div className="max-w-3xl fade-in-up">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white text-xs tracking-[0.2em] uppercase mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> All destinations
            </Link>
            <span className="vp-label text-white/80">{dest.country}</span>
            <h1 className="font-serif-vp text-5xl md:text-7xl leading-[1.05] mt-3 mb-3" data-testid="destination-name">
              {dest.name}
            </h1>
            <p className="font-serif-vp italic text-2xl md:text-3xl text-[var(--vp-accent)]">{dest.tagline}</p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-8">
          <p className="font-serif-vp text-xl md:text-2xl text-[var(--vp-primary)] leading-relaxed">{dest.about}</p>
          <div>
            <span className="vp-label block mb-4">SIGNATURE MOMENTS</span>
            <ul className="space-y-3">
              {dest.highlights.map((h, i) => (
                <li key={h} className="flex items-start gap-3 text-[var(--vp-text)]">
                  <span className="text-[var(--vp-secondary)] font-serif-vp text-lg leading-none pt-1">0{i + 1}</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-4">
            <button
              onClick={() => setChatOpen(true)}
              className="vp-btn-primary"
              data-testid="destination-plan-btn"
            >
              Plan My {dest.name} Trip →
            </button>
          </div>
        </div>
        {/* Side card */}
        <aside className="md:col-span-1">
          <div className="vp-card p-6 space-y-5 sticky top-24">
            <div>
              <div className="flex items-center gap-2 vp-label mb-1"><Calendar className="w-3.5 h-3.5" /> BEST TIME</div>
              <div className="text-sm text-[var(--vp-primary)]">{dest.best_time}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 vp-label mb-1"><Plane className="w-3.5 h-3.5" /> IDEAL DURATION</div>
              <div className="text-sm text-[var(--vp-primary)]">{dest.ideal_days}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 vp-label mb-1"><Users className="w-3.5 h-3.5" /> IDEAL FOR</div>
              <div className="text-sm text-[var(--vp-primary)]">{dest.ideal_for}</div>
            </div>
          </div>
        </aside>
      </section>

      <Footer />
      <WhatsAppFloat />
      <AIChat open={chatOpen} onOpenChange={setChatOpen} destination={dest.name} />
    </div>
  );
}
